/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable camelcase */
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { addSuccess, addWarning, dismissType } from 'actions/Error';
import {
  PergamonFiscalRecord,
  PergamonFiscalRequest,
  PergamonFiscalResponse,
} from 'plugins/oneAcrePergamonFiscal/types';
import { shouldSaveSaleDocToFiscal } from 'plugins/oneAcrePergamonFiscal/utils';
import { PosPlugin } from 'plugins/plugin';
import { PluginError } from 'plugins/pluginUtils';
import { getSelectedCustomer } from 'reducers/customerSearch';

import { recordSaleInPergamonFiscal } from './API';

interface ErplyApiRequest {
  requestName: string;
  [key: string]: any;
}

interface SaveSalesDocumentRequest {
  requestName: 'saveSalesDocument';
  jdoc?: string;
  [key: string]: any;
}

function isSaveSalesDocumentRequest(
  request: ErplyApiRequest,
): request is SaveSalesDocumentRequest {
  return request.requestName === 'saveSalesDocument';
}

function saveSaleToFiscalService(
  saveSalesDocumentRequest: SaveSalesDocumentRequest,
) {
  return async (
    dispatch: ThunkDispatch<unknown, unknown, Action>,
    getState,
  ) => {
    try {
      await dispatch(
        addWarning('Saving sale to Pergamon Fiscal...', {
          dismissible: false,
          selfDismiss: false,
          errorType: 'pergamon-fiscal',
        }),
      );

      const customer = getSelectedCustomer(getState());

      const pergamonData: {
        response: PergamonFiscalResponse | string;
        request: PergamonFiscalRequest;
      } = await dispatch(
        recordSaleInPergamonFiscal(saveSalesDocumentRequest, customer),
      );

      const { customer_pin, invoice_pin } = pergamonData.request;
      const { verify_url, cu_serial_number, cu_invoice_number } =
        // workaround because currently API returns invalid JSON string
        typeof pergamonData.response === 'string'
          ? JSON.parse((pergamonData.response as string).replace(/\\/g, ''))
          : pergamonData.response;

      const [serialNumber, date, time] = cu_serial_number.split(' ');
      // TODO when 'XX' is removed from date string
      // const cu_datetime = dayjs(`${date} ${time}`).format(
      //   'YYYY-MM-DD HH:mm:ss',
      // );

      const fiscalData: PergamonFiscalRecord = {
        cu_datetime: `${date} ${time}`,
        cu_serial_number: serialNumber,
        cu_invoice_number,
        verify_url,
        customer_pin,
        invoice_pin,
      };

      await dispatch(addSuccess('Sale saved to Pergamon Fiscal!'));

      return fiscalData;
    } catch (e) {
      const messageForUI = !e.response
        ? 'Unable to get a response from the fiscal server'
        : e.response?.data?.error_status;

      throw new PluginError(
        e.message,
        `Failed to save the sale to Pergamon Fiscal.${
          messageForUI ? `\n${messageForUI}` : ''
        }`,
      );
    } finally {
      await dispatch(dismissType('pergamon-fiscal'));
    }
  };
}

function addFiscalDataToRequest(
  request: SaveSalesDocumentRequest,
  fiscalData: PergamonFiscalRecord,
) {
  const fiscalJdoc = { 'kenya-fiscal-brazil': fiscalData };
  if (!request.jdoc) {
    return { ...request, jdoc: JSON.stringify(fiscalJdoc) };
  }
  let jdocObject;
  try {
    jdocObject = JSON.parse(request.jdoc);
  } catch (error) {
    jdocObject = {};
  }
  const mergedJdoc = {
    ...jdocObject,
    ...fiscalJdoc,
  };
  return {
    ...request,
    jdoc: JSON.stringify(mergedJdoc),
  };
}

export const saveFiscalData: Required<
  PosPlugin
>['onSaveSalesDocument']['on'] = (p, ap) => async (
  dispatch: ThunkDispatch<unknown, unknown, Action>,
) => {
  // check if finalized sale
  const saleDoc = ap.find(isSaveSalesDocumentRequest);
  if (!(saleDoc && shouldSaveSaleDocToFiscal(saleDoc))) {
    return ap;
  }
  const fiscalData = await dispatch(saveSaleToFiscalService(saleDoc));
  return ap.map(request => {
    if (!isSaveSalesDocumentRequest(request)) {
      return request;
    }
    return addFiscalDataToRequest(request, fiscalData);
  });
};
